import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

import Option from './virtualizedOption.component';

const allOption = { value: 'ALL', label: 'All' };

const resolveOption = (val, options, valueKey) => {
  if (val === null || typeof val === 'undefined' || val === '') {
    return null;
  }
  if (typeof val === 'object') {
    return val;
  }
  return options.find(option => option[valueKey] === val) || null;
};

const resolveValue = (value, options, valueKey, isMulti) => {
  if (isMulti) {
    if (!value) {
      return [];
    }
    const values = Array.isArray(value) ? value : [value];
    return values.map(item => resolveOption(item, options, valueKey)).filter(Boolean);
  }
  return resolveOption(value, options, valueKey);
};

class CustomSelect extends PureComponent {
  static propTypes = {
    options: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
    onChange: PropTypes.func,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    labelKey: PropTypes.string,
    valueKey: PropTypes.string,
    descriptionKey: PropTypes.string,
    autoSelectFirstValue: PropTypes.bool,
    autoSelectAllValues: PropTypes.bool,
    removeSelected: PropTypes.bool,
    withAll: PropTypes.bool,
    multi: PropTypes.bool,
    clearable: PropTypes.bool,
    disabled: PropTypes.bool,
    searchable: PropTypes.bool,
    virtualized: PropTypes.bool,
    optionHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    optionRenderer: PropTypes.func,
    valueComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    filterOptions: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    filterOption: PropTypes.func,
    onSelectResetsInput: PropTypes.bool,
    scrollMenuIntoView: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    closeOnSelect: PropTypes.bool,
    openOnFocus: PropTypes.bool,
    inputProps: PropTypes.shape({
      className: PropTypes.string,
    }),
    selectComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    isValidNewOption: PropTypes.func,
    promptTextCreator: PropTypes.func,
    components: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  };

  static defaultProps = {
    withAll: false,
    labelKey: 'label',
    valueKey: 'value',
    descriptionKey: 'description',
    autoSelectFirstValue: false,
    autoSelectAllValues: false,
    removeSelected: false,
    multi: false,
    clearable: false,
    disabled: false,
    searchable: true,
  };

  componentDidMount() {
    const {
      autoSelectFirstValue,
      autoSelectAllValues,
      value,
    } = this.props;
    const options = Array.isArray(this.props.options) ? this.props.options : [];

    if (autoSelectFirstValue && options.length && !value) {
      this.autoSelect();
    }

    if (autoSelectAllValues && options.length && (!value || !value.length)) {
      this.autoSelectAll();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      valueKey,
      autoSelectFirstValue,
      autoSelectAllValues,
      value,
    } = this.props;
    const options = Array.isArray(this.props.options) ? this.props.options : [];
    const prevOptions = Array.isArray(prevProps.options) ? prevProps.options : [];

    if (autoSelectFirstValue || autoSelectAllValues) {
      const previousOptions = prevOptions[0] ? prevOptions.map(option => option[valueKey]) : [];

      if (
        autoSelectFirstValue
        && options.length
        && !previousOptions.length
        && !value
      ) {
        this.autoSelect();
      }

      if (
        autoSelectAllValues
        && options.length
        && !previousOptions.length
        && (!value || !value.length)
      ) {
        this.autoSelectAll();
      }
    }
  }

  autoSelect = () => {
    const { onChange, options } = this.props;

    if (onChange) {
      onChange(options[0]);
    }
  };

  autoSelectAll = () => {
    const { onChange, options } = this.props;

    if (onChange) {
      onChange(options);
    }
  };

  handleChange = selectedItems => {
    const { onChange, options, withAll } = this.props;

    if (!onChange) {
      return;
    }

    if (withAll && Array.isArray(selectedItems) && selectedItems.includes(allOption)) {
      return onChange(options);
    }

    return onChange(selectedItems);
  };

  getOptionLabel = option => option[this.props.labelKey];

  getOptionValue = option => option[this.props.valueKey];

  resolveComponents = componentsOverride => {
    if (this.cachedComponentsOverride !== componentsOverride) {
      this.cachedComponentsOverride = componentsOverride;
      this.cachedComponents = { Option, ...componentsOverride };
    }
    return this.cachedComponents;
  };

  render() {
    const {
      options,
      withAll,
      value,
      onChange,
      labelKey,
      valueKey,
      descriptionKey,
      multi,
      clearable,
      disabled,
      searchable,
      removeSelected,
      virtualized, // eslint-disable-line no-unused-vars
      optionHeight, // eslint-disable-line no-unused-vars
      optionRenderer, // eslint-disable-line no-unused-vars
      valueComponent, // eslint-disable-line no-unused-vars
      filterOptions, // eslint-disable-line no-unused-vars
      filterOption, // eslint-disable-line no-unused-vars
      onSelectResetsInput, // eslint-disable-line no-unused-vars
      autoSelectFirstValue, // eslint-disable-line no-unused-vars
      autoSelectAllValues, // eslint-disable-line no-unused-vars
      scrollMenuIntoView,
      onOpen,
      onClose,
      closeOnSelect,
      openOnFocus,
      inputProps,
      selectComponent,
      isValidNewOption,
      promptTextCreator,
      components: componentsOverride,
      ...rest
    } = this.props;

    const safeOptions = Array.isArray(options) ? options : [];
    const allOptions = withAll ? [allOption, ...safeOptions] : safeOptions;
    const SelectComponent = selectComponent || Select;

    const classNames = { container: () => 'Select' };
    if (inputProps && inputProps.className) {
      classNames.input = () => inputProps.className;
    }

    const creatableProps = {};
    if (selectComponent) {
      if (promptTextCreator) {
        creatableProps.formatCreateLabel = inputValue => promptTextCreator(inputValue);
      }
      if (isValidNewOption) {
        creatableProps.isValidNewOption = inputValue => isValidNewOption({ [labelKey]: inputValue });
      }
    }

    if (removeSelected) {
      creatableProps.hideSelectedOptions = true;
    }

    return (
      <SelectComponent
        {...rest}
        classNamePrefix="Select"
        options={allOptions}
        value={resolveValue(value, allOptions, valueKey, multi)}
        onChange={this.handleChange}
        getOptionLabel={this.getOptionLabel}
        getOptionValue={this.getOptionValue}
        descriptionKey={descriptionKey}
        isMulti={multi}
        isClearable={clearable}
        isDisabled={disabled}
        isSearchable={searchable}
        menuShouldScrollIntoView={scrollMenuIntoView}
        onMenuOpen={onOpen}
        onMenuClose={onClose}
        closeMenuOnSelect={closeOnSelect}
        openMenuOnFocus={openOnFocus}
        classNames={classNames}
        components={this.resolveComponents(componentsOverride)}
        {...creatableProps}
      />
    );
  }
}

export default CustomSelect;
